import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Grid,
  IconButton,
  List as MuiList,
  ListItem as MuiListItem,
} from "@mui/material";
import { Alert, Button, Typography } from "@platform/design-system";

import { useCopyTextToClipboard } from "../../../../hooks/useCopyTextToClipboard";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import { CarrierWithContactsByType } from "../../../../types/carrier";

const TOAST_AUTO_HIDE_DURATION = 2000;

const CarrierContactsListItem = ({
  label,
  contacts,
  onCopyContact,
  isSalesRep = false,
}: {
  label: string;
  contacts: string[];
  isSalesRep?: boolean;
  onCopyContact: (contact: string) => void;
}): React.JSX.Element => (
  <MuiListItem
    disablePadding
    alignItems="flex-start"
    sx={{
      flexDirection: { xs: "column", sm: "row" },
    }}
  >
    <Typography
      variant="body-medium"
      px={1}
      py={0.5}
      minWidth={100}
      color="textSecondary"
    >
      {label}
    </Typography>
    <Box
      sx={{
        maxWidth: { xs: "100%", sm: "calc(100% - 100px)" },
      }}
    >
      {contacts.length > 0 ? (
        contacts.map((contact) => (
          <Button
            key={`contact-${label}-${contact}`}
            title={contact}
            variant="ghost"
            onClick={() => {
              onCopyContact(contact);
            }}
            sx={{
              px: 1,
              py: 0.5,
              color: "text.primary",
              textTransform: "none",
              fontWeight: "fontWeightRegular",
              maxWidth: "100%",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {contact}
          </Button>
        ))
      ) : (
        <Typography
          variant="body-medium"
          sx={{ px: 1, py: 0.5 }}
          color={isSalesRep ? "danger" : undefined}
        >
          not assigned
        </Typography>
      )}
    </Box>
  </MuiListItem>
);

const GridItem = ({
  title,
  carriers,
}: {
  title: string;
  carriers: CarrierWithContactsByType[];
}): React.JSX.Element => {
  const { createToastNotification } = useToastNotificationService();
  const copyToClipboard = useCopyTextToClipboard({
    autoHideDuration: TOAST_AUTO_HIDE_DURATION,
  });

  const handleCopyContact = (contact: string): void => {
    copyToClipboard(contact, "Email copied!", "Failed to copy email.");
  };

  const handleCopyAllCarrierContacts = (
    carrier: CarrierWithContactsByType,
  ): void => {
    const contacts = Object.values(carrier.contactsByType).flat();
    if (contacts.length === 0) {
      createToastNotification({
        message: "No emails to copy.",
        severity: "warning",
        autoHideDuration: TOAST_AUTO_HIDE_DURATION,
      });
      return;
    }

    const allContacts = contacts.join("; ");
    copyToClipboard(
      allContacts,
      "All emails copied!",
      "Failed to copy emails.",
    );
  };

  return (
    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
      <Typography variant="header-small">
        <strong>{title}</strong>
      </Typography>
      {carriers.length > 0 ? (
        <MuiList>
          {carriers.map((carrier) => (
            <MuiListItem
              key={`carrier-${carrier.carrierId ?? carrier.carrierName}`}
              sx={{
                px: 1,
                "&:hover": {
                  backgroundColor: "surfaces.neutral",
                },
                "&:hover .copy-all-contacts-button": {
                  opacity: 1,
                },
              }}
            >
              <Box display="flex" width="100%" maxWidth="100%">
                <Box width="100%" maxWidth="calc(100% - 30px)">
                  <Typography variant="body-large" px={1}>
                    {carrier.carrierName}
                  </Typography>
                  <MuiList>
                    <CarrierContactsListItem
                      label="Sales Rep"
                      contacts={carrier.contactsByType.sales_rep}
                      isSalesRep
                      onCopyContact={handleCopyContact}
                    />
                    <CarrierContactsListItem
                      label="Support"
                      contacts={carrier.contactsByType.support}
                      onCopyContact={handleCopyContact}
                    />
                    <CarrierContactsListItem
                      label="General"
                      contacts={carrier.contactsByType.general}
                      onCopyContact={handleCopyContact}
                    />
                  </MuiList>
                </Box>
                <Box display="flex" alignItems="center">
                  <IconButton
                    className="copy-all-contacts-button"
                    onClick={() => handleCopyAllCarrierContacts(carrier)}
                    size="small"
                    title="Copy all emails"
                    aria-label="Copy all emails"
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </MuiListItem>
          ))}
        </MuiList>
      ) : (
        <Typography>-</Typography>
      )}
    </Grid>
  );
};

const CardContent = ({
  missingSalesRep,
  connectedCarriers,
  unconnectedCarriers,
  unknownCarriers,
}: {
  missingSalesRep: boolean;
  connectedCarriers: CarrierWithContactsByType[];
  unconnectedCarriers: CarrierWithContactsByType[];
  unknownCarriers: CarrierWithContactsByType[];
}): React.JSX.Element => (
  <>
    {missingSalesRep ? (
      <Alert severity="error" sx={{ mb: 3 }}>
        One or more carriers are missing a Carrier Sales Rep email and will not
        be sent this RFP unless one is added
      </Alert>
    ) : null}
    <Grid container spacing={4}>
      <GridItem title="Connected" carriers={connectedCarriers} />
      <GridItem title="Unconnected" carriers={unconnectedCarriers} />
      <GridItem title="Unknown" carriers={unknownCarriers} />
    </Grid>
  </>
);

export { CardContent };
